/**
 * Scoping rules for the System Status Selection Detail — which *component* a
 * status pane's numbers and rosters come from (feature-spec §2.2; REQ-STATUS-039
 * / REQ-STATUS-044).
 *
 * Pure and React-free, because these are the decisions that were previously made
 * inline with an index literal (``byKind.get(kind)?.[0]``) and were wrong for
 * every node backed by more than one component. Extracting them makes each rule
 * directly unit-testable and gives it one place to be corrected.
 *
 * # The three-level hierarchy this file scopes against
 *
 * component → interface → connection (architect ruling 2026-07-31). A component
 * hosts N interfaces; an **interface** hosts N connections. So "whose
 * connections am I showing?" has two different answers depending on the level
 * the operator selected, and :func:`hostsComponentLevelRoster` is the
 * predicate that says which one applies.
 *
 * Cross-references:
 *   - feature-spec §2.2 (hierarchy, data sources, the latent scoping defect).
 *   - REQ-STATUS-039 (interface selection); REQ-STATUS-044 (roster placement);
 *     REQ-STATUS-043 `[DEFERRED]` (ingress interfaces do not exist yet).
 *   - ADR-024 §4/§5 (amended 2026-07-31 — three Status entity types).
 */
import {
  parseComponentKind,
  parseRollupState,
  type ResolvedComponent,
  type RollupState,
} from "@/system-status/status-model";
import {
  CONNECTOR_KINDS,
  INTERFACE_NAMED_PEER_KINDS,
  isPeerNode,
  nodeConnectionsKind,
  type TopologyNode,
  type ConfiguredInterface,
} from "@/system-status/status-topology";
import type { PeerInterfaceView } from "@/peers/peer-model";
import { findInterfaceType } from "@/peers/peer-catalog";

/**
 * The component instance a node's Selection Detail describes — the one whose
 * rollup **won** the node's stoplight, falling back to the first reported.
 *
 * A node can be backed by several instances (replicas) and, since the v2
 * topology folded the three egress kinds into one node, by several *kinds*. The
 * winner is what the header pill already shows, so pinning the sections to it is
 * what keeps one instance's numbers from sitting under another instance's pill
 * (Flow L §11; ADR-036 §D-7).
 *
 * The fallback matters when nothing matches — an empty list, or a node state
 * derived from something other than a member rollup. Callers must treat the
 * fallback as "this instance may not be the one the pill describes" and not
 * quote the node state beside its reason (REQ-STATUS-027 defect (a)).
 */
export function primaryComponent(
  components: readonly ResolvedComponent[] | undefined,
  nodeState: RollupState,
): ResolvedComponent | undefined {
  if (components === undefined || components.length === 0) return undefined;
  return components.find((c) => parseRollupState(c.rollup_state) === nodeState) ?? components[0];
}

/**
 * Whether a node has an **interface layer** — a configured-interface model that
 * icons, and the connection rosters beneath them, hang off (feature-spec §2.2).
 *
 * Egress only, today. ``Ingress`` declares an ``interfaceEdge`` so its future
 * stack has a side to hang off, so ``interfaceEdge`` cannot be the test:
 * ADR-037's ingress interface model does not exist yet (REQ-STATUS-043
 * ``[DEFERRED]`` — no ``control_plane.ingress_*`` table, no
 * ``/api/admin/ingress/*`` route), so Ingress composes no interfaces at all.
 */
export function hostsInterfaceLayer(node: TopologyNode): boolean {
  return isPeerNode(node);
}

/**
 * Does this node host the **client-observed live-stream** section?
 *
 * Only the User Interface Manager. The BFF *is* that component and it serves
 * both EventSources the browser holds — the map stream and the status stream —
 * so "how is my connection to it?" is genuinely a property of this node rather
 * than free-floating chrome state (REQ-STATUS-050).
 *
 * A named predicate rather than an inline id comparison, matching
 * :func:`hostsComponentLevelRoster` and :func:`isPeerNode`: the node id is a
 * layout identifier that has already been renamed once (``bff`` →
 * ``user-interface-manager``), so the knowledge of which node means what belongs
 * in one place.
 */
export function isUserInterfaceManagerNode(node: TopologyNode): boolean {
  return node.id === "user-interface-manager";
}

/**
 * Whether this node's connection roster hangs off the **component** rather than
 * one level down on each of its interfaces (REQ-STATUS-044's interim clause).
 *
 * The test is "does this node have an interface layer *at all*?", not "how many
 * interfaces are configured right now": a node with an interface layer and zero
 * configured interfaces has no connections either, so hoisting its roster back
 * up to the component would show an empty section rather than rescue anything.
 *
 * Today that makes it exactly ``Ingress``, which is the point — the deleted
 * *SAPIENT Ingress Connections* satellite was the only entry point to the live
 * per-producer connection list, and losing it would be an operator-visibility
 * regression (CLAUDE.md §8.1; REQ-STATUS-044). When ADR-037 Phases E/F land and
 * ingress interfaces exist, this predicate goes false for ``Ingress`` on its own
 * and the roster moves down a level with no edit here.
 */
export function hostsComponentLevelRoster(node: TopologyNode): boolean {
  return nodeConnectionsKind(node) !== null && !hostsInterfaceLayer(node);
}

/**
 * The ``component_id`` whose connections a **component-level** roster fetches,
 * or ``null`` when the node has none to show.
 *
 * ⚠ This replaces ``model.byKind.get(connKind)?.[0]?.component_id`` — the latent
 * defect called out in REQ-STATUS-039's Notes and feature-spec §2.2. Taking the
 * *first* component of a kind is already wrong today: several per-interface
 * egress components share one kind, so the roster could be fetched for a
 * completely different flow than the one the pane describes, with nothing on
 * screen saying so. The roster is now scoped to the **same instance the sections
 * describe** (:func:`primaryComponent`), and only when that instance's kind
 * actually carries connections — a kind that reports no ``conn_*`` at all would
 * otherwise render a section permanently reading "not collected".
 */
export function componentRosterId(
  node: TopologyNode,
  primary: ResolvedComponent | undefined,
): string | null {
  if (!hostsComponentLevelRoster(node) || primary === undefined) return null;
  const kind = parseComponentKind(primary.component_kind);
  return kind !== null && CONNECTOR_KINDS.has(kind) ? primary.component_id : null;
}

/**
 * The ``component_id`` an **interface's** roster and vitals are fetched under —
 * the interface's own name.
 *
 * ``meridian_peer_manager`` emits one ``ComponentStatusReport`` per configured
 * direct interface, named by the interface, so ``component_id == config.name``
 * (feature-spec §2.2). That identity is why an interface roster needs **no API
 * change**: it reuses the per-component route (REQ-STATUS-021) with this id.
 *
 * Written as a named function rather than passing the name around directly so
 * the join is visible at every call site — it is a *convention* of the emitting
 * service, not a law, and if it ever stops holding this is the one place to fix.
 */
export function interfaceComponentId(interfaceName: string): string {
  return interfaceName;
}

/**
 * The interface's reporting component, or ``undefined`` when the status feed has
 * not reported it (REQ-STATUS-038 — a **left join** from the config roster onto
 * the feed, so an interface that exists but has not yet reported is honestly
 * unknown rather than invisible or green).
 *
 * The candidate set is narrowed to the kinds whose ``component_id`` *is* an
 * operator interface name (:data:`INTERFACE_NAMED_PEER_KINDS`) before matching.
 * Interface names are operator-chosen free text and component ids are service
 * identities drawn from the same string space, so an interface named
 * ``message-bus`` would otherwise join onto the Message Bus service and show its
 * CPU, its metrics and its connections as the interface's own — a false
 * attribution on a peer-manager surface (CLAUDE.md §8.1).
 */
export function interfaceComponent(
  components: readonly ResolvedComponent[],
  interfaceName: string,
): ResolvedComponent | undefined {
  const id = interfaceComponentId(interfaceName);
  return components.find(
    (c) =>
      c.component_id === id &&
      (INTERFACE_NAMED_PEER_KINDS as ReadonlySet<string>).has(c.component_kind),
  );
}

/** One interface of the selected peer, with its health already joined on. */
export interface PeerInterfaceRow {
  /** The configured interface — authoritative for existence. */
  config: PeerInterfaceView;
  /** Its reporting component, or undefined when it has not reported yet. */
  component: ResolvedComponent | undefined;
  /** The joined rollup; ``unknown`` when unreported (never a false green). */
  state: RollupState;
}

/**
 * Join a peer's configured interfaces onto the status feed (REQ-STATUS-086).
 *
 * Exported so the join is directly testable rather than only observable through
 * a rendered pane — it is the rule the whole display rests on, and a test that
 * has to render to assert it will not survive the next layout change.
 */
export function joinPeerInterfaces(
  configured: readonly PeerInterfaceView[],
  merged: readonly ResolvedComponent[],
): PeerInterfaceRow[] {
  return configured.map((config) => {
    const component = merged.find((c) => c.component_id === interfaceComponentId(config.name));
    return {
      config,
      component,
      state: component === undefined ? "unknown" : parseRollupState(component.rollup_state),
    };
  });
}

/** The two domain keys REQ-UI-149 governs. */
export const SPOOL_METRIC_KEYS = ["spool_depth", "spool_oldest_age_seconds"] as const;

/**
 * Whether this interface runs a spool — ``null`` when this session cannot tell.
 *
 * ⚠ **The emitter sends the spool metrics either way, and that is correct on
 * its side.** ``_interface_domain`` reports a sampled value whenever it is
 * non-negative, so a *direct* interface — which has no spool and whose series
 * nobody writes — sums to a perfectly honest ``0.0`` and is reported. That rule
 * is the right one for a sampler: keying presence on the series existing would
 * make these the only domain keys whose absence meant "not applicable" rather
 * than "not known", and the sentinel already carries the could-not-ask case.
 *
 * So the omission belongs **here**, where the terminal is known. ``Spool depth
 * 0`` on an interface that has no spool is trivially true and operationally
 * misleading — it invites an operator to conclude a spool has drained, on a
 * path where nothing is ever spooled.
 *
 * Resolved in three steps, and the third is *stated* rather than guessed:
 *
 * 1. the interface's own ``terminal``, when the response carried it;
 * 2. otherwise the **type**'s ``requiresSpool``, which is authoritative for the
 *    CDS types and derivable from the all-roles ``type_id``;
 * 3. otherwise ``null`` — a direct-capable type on a session that cannot see
 *    the terminal genuinely cannot be classified, and silently omitting there
 *    would be indistinguishable from the direct case, which is the confusion
 *    REQ-UI-149 exists to remove.
 */
export function resolveSpooled(configured: ConfiguredInterface | null): boolean | null {
  if (configured === null) return null;
  if (configured.terminal === "spooled") return true;
  if (configured.terminal === "direct") return false;
  // A type that may ONLY run spooled answers for every session (REQ-PEER-081).
  if (findInterfaceType(configured.typeId)?.requiresSpool === true) return true;
  return null;
}
